import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate } from "react-router-dom";
import {
  addLocalNotification,
  clearInitAccountStatus,
  clearNewMainBusiness,
  clearUpdateBusiness,
  requestAddMainBusiness,
  requestCreateBusiness,
  requestGetBusiness,
  requestInitAccount,
  requestListAccount,
  requestListBusiness,
  requestUpdateBusiness,
  resetNewBusiness,
  useAccountingDispatch,
  useAccountingState,
  useAuthDispatch,
  useAuthState,
  useBusinessTransactionDispatch,
  useBusinessTransactionState,
  useNotificationDispatch,
} from "../../../../Context";
import { TAX_CATEGORIES } from "../../../../Constants";
import { Field, Fieldset } from "@headlessui/react";
import { generateArrayOfYears, validateImage } from "../../../../Utils";
import EmptyState from "../../../EmptyState";
import {
  BUSINESS_CATEGORIES,
  BUSINESS_ENTITY_TYPES,
  BUSINESS_TYPES_OPTIONS,
} from "../../../../Constants/business";
import { useFreeBusinessCreateBlocked } from "../../../../Hooks";
import { ModalConfirm } from "../../../ModalConfirm";
import Button from "../../Button";
import SlideOver from "../../Modal/SlideOver";
import FormInput from "../../Form/FormInput";
import FormPhoneInput from "../../Form/FormPhoneInput";
import FormSelect from "../../Form/FormSelect";
import FormRadioGroup from "../../Form/FormRadioGroup";
import FormError from "../../Form/FormError";
import FormToggle from "../../Form/FormToggle";
import { ExternalLinkIcon } from "@heroicons/react/outline";

const years = generateArrayOfYears();

const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const sortAccountCode = (a, b) => {
  const aCode = parseInt(a.code, 10);
  const bCode = parseInt(b.code, 10);
  return (aCode < 400000) - (bCode < 400000) || aCode - bCode;
};

const toOption = (a) => ({ value: a.id, label: `${a.code} - ${a.name}` });

function SectionCard({ title, children }) {
  return (
    <div className="pb-6">
      <h3 className="text-base font-semibold leading-6 text-gray-900 my-4">
        {title}
      </h3>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function NoLogo() {
  return (
    <div className="rounded-lg w-24 h-24 flex-shrink-0 overflow-hidden bg-gray-100 flex items-center justify-center">
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 180.119 139.794"
        fill="currentColor"
        className="h-full w-full text-gray-300"
      >
        <g
          transform="translate(-13.59 -66.639)"
          paintOrder="fill markers stroke"
        >
          <path
            d="m118.507 133.514-34.249 34.249-15.968-15.968-41.938 41.937H178.726z"
            opacity=".675"
            fill="#C3C4C4"
          />
          <circle
            cx="58.217"
            cy="108.555"
            r="11.773"
            opacity=".675"
            fill="#C3C4C4"
          />
          <path fill="none" d="M26.111 77.634h152.614v116.099H26.111z" />
        </g>
      </svg>
    </div>
  );
}

function InputSkeleton({ rows }) {
  return (
    <>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="animate-pulse space-y-2">
          <div className="w-36 h-4 bg-gray-300 rounded-md" />
          <div className="block h-10 bg-gray-300 rounded-md" />
        </div>
      ))}
    </>
  );
}

const BusinessFormModal = ({ open, onClose, id, onboarding, onCreated }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user, mainBusinessStatus } = useAuthState();
  const authDispatch = useAuthDispatch();

  const isEditing = Boolean(id);
  const isPremium = user?.membership === "PREMIUM";

  const [name, setname] = useState("");
  const [type, settype] = useState(BUSINESS_TYPES_OPTIONS[0]);
  const [entityType, setentityType] = useState(null);
  const [category, setcategory] = useState(null);
  const [phone, setphone] = useState("");
  const [address, setaddress] = useState("");
  const [taxCategory, settaxCategory] = useState(null);
  const [npwp, setnpwp] = useState("");
  const [yearRegistered, setyearRegistered] = useState(null);
  const [fiscalYearStartMonth, setfiscalYearStartMonth] = useState(null);
  const [email, setemail] = useState("");

  const [image, setimage] = useState(null);
  const [imageError, setimageError] = useState(null);
  const [premiumModalOpen, setpremiumModalOpen] = useState(false);
  const [premiumModalTitle, setpremiumModalTitle] = useState("");
  const [premiumModalDescription, setpremiumModalDescription] = useState("");
  const [premiumModalType, setpremiumModalType] = useState("");

  const [alwaysSync, setalwaysSync] = useState(false);
  const [mainBusinessActive, setmainBusinessActive] = useState(false);
  const [cashIncomeDebitAccount, setcashIncomeDebitAccount] = useState(null);
  const [cashIncomeCreditAccount, setcashIncomeCreditAccount] = useState(null);
  const [cashExpenseDebitAccount, setcashExpenseDebitAccount] = useState(null);
  const [cashExpenseCreditAccount, setcashExpenseCreditAccount] =
    useState(null);
  const [salesDiscountAccount, setsalesDiscountAccount] = useState(null);
  const [purchaseDiscountAccount, setpurchaseDiscountAccount] = useState(null);
  // True while a save triggered by "generate accounts" is in flight, so the
  // update-success effect chains init instead of closing the form.
  const [initAfterSave, setInitAfterSave] = useState(false);

  const [errors, setErrors] = useState({});

  const cleanedPhone = phone?.replace(/\s/g, "");

  const dispatch = useBusinessTransactionDispatch();
  const {
    updateBusiness,
    business,
    businesses,
    error,
    loading,
    businessError,
  } = useBusinessTransactionState();

  // Free accounts may keep only 1 business; block creating another and steer to
  // premium. Only relevant in create mode (edit/onboarding never blocked).
  const freeCreateBlocked = useFreeBusinessCreateBlocked(businesses);
  const createBlocked = !isEditing && freeCreateBlocked;

  const notificationDispatch = useNotificationDispatch();
  const accountingDispatch = useAccountingDispatch();
  const {
    accounts,
    accountLoading,
    accountError,
    initAccountLoading,
    initAccountStatus,
    submitError,
  } = useAccountingState();

  const taxCategories = useMemo(
    () => TAX_CATEGORIES.map((item) => ({ ...item, label: t(item.label) })),
    [t],
  );

  // Fiscal-year start month (1-12). Drives the balance-sheet retained-earnings
  // split; unset = January.
  const fiscalMonthOptions = useMemo(
    () =>
      Array.from({ length: 12 }, (_, i) => ({
        value: i + 1,
        label: t(`common.months.${i + 1}`, {
          defaultValue: new Date(2000, i, 1).toLocaleString("id-ID", {
            month: "long",
          }),
        }),
      })),
    [t],
  );

  const cashIncomeDebitOptions = useMemo(
    () =>
      accounts
        .filter((a) => ["CASH_AND_BANK", "INVENTORY"].includes(a.category))
        .sort(sortAccountCode)
        .map(toOption),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );

  const cashIncomeCreditOptions = useMemo(
    () =>
      accounts
        .filter((a) => ["INCOME", "OTHER_INCOME"].includes(a.category))
        .sort(sortAccountCode)
        .map(toOption),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );

  const cashExpenseDebitOptions = useMemo(
    () =>
      accounts
        .filter((a) =>
          [
            "COST_OF_GOODS_SOLD",
            "EXPENSE",
            "OTHER_EXPENSE",
            "INVENTORY",
            "CASH_AND_BANK",
            "ACCOUNTS_RECEIVABLE",
            "FIXED_ASSETS",
          ].includes(a.category),
        )
        .sort(sortAccountCode)
        .map(toOption),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );

  const cashExpenseCreditOptions = useMemo(
    () =>
      accounts
        .filter((a) =>
          [
            "CASH_AND_BANK",
            "INVENTORY",
            "OTHER_CURRENT_ASSETS",
            "FIXED_ASSETS",
            "OTHER_ASSETS",
          ].includes(a.category),
        )
        .sort(sortAccountCode)
        .map(toOption),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );

  // Sales discount is a contra-revenue account (default 440100 Diskon
  // Penjualan) -> pick from INCOME accounts.
  const salesDiscountOptions = useMemo(
    () =>
      accounts
        .filter((a) => ["INCOME", "OTHER_INCOME"].includes(a.category))
        .sort(sortAccountCode)
        .map(toOption),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );

  // Purchase discount is a contra-COGS account (default 550100 Diskon
  // Pembelian) -> pick from COGS/expense accounts.
  const purchaseDiscountOptions = useMemo(
    () =>
      accounts
        .filter((a) =>
          ["COST_OF_GOODS_SOLD", "EXPENSE", "OTHER_EXPENSE"].includes(
            a.category,
          ),
        )
        .sort(sortAccountCode)
        .map(toOption),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [accounts],
  );

  const imageUrl = useMemo(() => {
    if (image === null) return null;
    if (image) {
      return URL.createObjectURL(image);
    }
    if (isEditing && business?.imageUrl) {
      return business.imageUrl;
    }
    return null;
  }, [image, isEditing, business?.imageUrl]);

  useEffect(() => {
    return () => {
      resetNewBusiness(dispatch);
      clearUpdateBusiness(dispatch);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (imageUrl && image instanceof File) {
      return () => URL.revokeObjectURL(imageUrl);
    }
  }, [imageUrl, image]);

  useEffect(() => {
    if (isEditing && id) {
      requestGetBusiness(dispatch, { id });
    }
  }, [dispatch, id, isEditing]);

  // Create mode: load the owner's businesses so the free-limit gate has a
  // reliable count (AccountingWrapper only lists them when there is no main
  // business, so we can't rely on the slice already being populated).
  useEffect(() => {
    if (open && !isEditing && user?.id) {
      requestListBusiness(dispatch, { owner: user.id });
    }
  }, [dispatch, open, isEditing, user?.id]);

  useEffect(() => {
    if (isEditing && id === user?.mainBusiness?.id) {
      setmainBusinessActive(true);
    }
  }, [id, user?.mainBusiness?.id, isEditing]);

  // Populate form in edit mode
  useEffect(() => {
    if (!isEditing) return;
    if (business.name) setname(business.name);
    if (business.email) setemail(business.email);
    if (business.type) {
      const found = BUSINESS_TYPES_OPTIONS.find(
        (o) => o.value === business.type,
      );
      settype(found || BUSINESS_TYPES_OPTIONS[0]);
    }
    if (business.structure) {
      const found = BUSINESS_ENTITY_TYPES.find(
        (item) => item.value === business.structure,
      );
      setentityType(found || null);
    }
    if (business.category) {
      const found = BUSINESS_CATEGORIES.find(
        (item) => item.value === business.category,
      );
      setcategory(found || null);
    }
    if (business.phone) setphone(business.phone);
    if (business.taxCategory) {
      const found = taxCategories.find(
        (item) => item.value === business.taxCategory,
      );
      settaxCategory(found || null);
    }
    if (business.address) setaddress(business.address);
    if (business.taxNumber) setnpwp(business.taxNumber);
    if (business.yearRegistered) {
      const found = years.find(
        (item) => item.value === business.yearRegistered,
      );
      setyearRegistered(found || null);
    }
    if (business.fiscalYearStartMonth) {
      const found = fiscalMonthOptions.find(
        (item) => item.value === business.fiscalYearStartMonth,
      );
      setfiscalYearStartMonth(found || null);
    }
    if (business.salesDiscountAccount)
      setsalesDiscountAccount({
        ...business.salesDiscountAccount,
        value: business.salesDiscountAccount.id,
        label: `${business.salesDiscountAccount.code} - ${business.salesDiscountAccount.name}`,
      });
    if (business.purchaseDiscountAccount)
      setpurchaseDiscountAccount({
        ...business.purchaseDiscountAccount,
        value: business.purchaseDiscountAccount.id,
        label: `${business.purchaseDiscountAccount.code} - ${business.purchaseDiscountAccount.name}`,
      });
    if (business.alwaysSync) setalwaysSync(true);
    if (business.image?.key) setimage("");
  }, [business, taxCategories, fiscalMonthOptions, isEditing]);

  // Populate account sync fields
  useEffect(() => {
    if (!isEditing || !business || !alwaysSync) return;
    const {
      cashExpenseCreditAccount: ceca,
      cashExpenseDebitAccount: ceda,
      cashIncomeCreditAccount: cica,
      cashIncomeDebitAccount: cida,
    } = business;
    if (ceca)
      setcashExpenseCreditAccount({
        ...ceca,
        value: ceca.id,
        label: `${ceca.code} - ${ceca.name}`,
      });
    if (ceda)
      setcashExpenseDebitAccount({
        ...ceda,
        value: ceda.id,
        label: `${ceda.code} - ${ceda.name}`,
      });
    if (cica)
      setcashIncomeCreditAccount({
        ...cica,
        value: cica.id,
        label: `${cica.code} - ${cica.name}`,
      });
    if (cida)
      setcashIncomeDebitAccount({
        ...cida,
        value: cida.id,
        label: `${cida.code} - ${cida.name}`,
      });
  }, [business, alwaysSync, isEditing]);

  useEffect(() => {
    // Load accounts in edit mode regardless of alwaysSync: the cash-sync
    // mapping needs them, and so do the discount-account pickers (which are
    // not tied to alwaysSync).
    if (isEditing && business?.id) {
      requestListAccount(accountingDispatch, {
        businessID: business.id,
        limit: 100,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [business?.id, alwaysSync, isEditing]);

  useEffect(() => {
    if (initAccountStatus === "SUCCESS") {
      requestListAccount(accountingDispatch, {
        businessID: business.id,
        limit: 100,
      });
      clearInitAccountStatus(accountingDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initAccountStatus]);

  // Runs from onSave rather than an effect on `newBusiness`: the empty-state
  // hosts (Accounting/Cashbook overlays) unmount this modal in the same render
  // the new business lands in the list, so an effect would never fire and the
  // COA-onboarding redirect was lost.
  const handleCreated = (created) => {
    addLocalNotification(notificationDispatch, {
      id: `create-business-${created.id}`,
      type: "success",
      title: "common.success",
      body: "business.messages.created",
      showFull: true,
    });
    // Make the newly created business the active/main business so the
    // app switches to it automatically after creation.
    requestAddMainBusiness(authDispatch, { businessID: created.id });
    if (onboarding) {
      navigate(`/coa-onboarding/${created.id}`);
    } else {
      resetForm();
      // Let the host offer the init-account flow for the new business.
      if (onCreated) onCreated(created);
    }
  };

  // Clear the main-business switch status set by the auto-switch above so a
  // later open of BusinessSwitchModal does not see a stale value.
  useEffect(() => {
    if (mainBusinessStatus) {
      clearNewMainBusiness(authDispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mainBusinessStatus]);

  // Save-then-init: if the profile save failed, drop the pending flag so a
  // later successful save does not accidentally chain an init.
  useEffect(() => {
    if (initAfterSave && (error || businessError)) setInitAfterSave(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error, businessError]);

  useEffect(() => {
    if (isEditing && updateBusiness?.id) {
      // Save-then-init: profile persisted, now seed the chart from it and keep
      // the form open (no notification / reset) so the user stays in context.
      if (initAfterSave) {
        setInitAfterSave(false);
        if (updateBusiness.id === user?.mainBusiness?.id) {
          authDispatch({
            type: "UPDATE_MAIN_BUSINESS",
            payload: { business: updateBusiness },
          });
        }
        clearUpdateBusiness(dispatch);
        requestInitAccount(accountingDispatch, {
          businessID: updateBusiness.id,
        });
        return;
      }
      addLocalNotification(notificationDispatch, {
        id: `update-business-${updateBusiness.id}`,
        type: "success",
        title: "common.success",
        body: "business.messages.updated",
        showFull: true,
      });
      // Sync image key into auth state so sidebar avatar reflects immediately
      if (updateBusiness.id === user?.mainBusiness?.id) {
        authDispatch({
          type: "UPDATE_MAIN_BUSINESS",
          payload: { business: updateBusiness },
        });
      }
      resetForm();
      clearUpdateBusiness(dispatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updateBusiness?.id]);

  // Clear entityType when switching away from BUSINESS type
  useEffect(() => {
    if (type?.value !== "BUSINESS") setentityType(null);
  }, [type]);

  const handleProfileLogoChange = (event) => {
    const selectedFile = event.target.files[0];
    const validation = validateImage(selectedFile, 1, [
      "image/jpeg",
      "image/png",
      "image/gif",
    ]);
    if (!validation.isValid) {
      setimageError(validation.errorMessage);
      setimage(null);
    } else {
      setimage(selectedFile);
      setimageError("");
    }
  };

  const handleNoPremium = () => {
    setpremiumModalType("WARNING");
    setpremiumModalTitle(t("business.messages.premiumFeature"));
    setpremiumModalDescription(t("business.messages.premiumLogoDesc"));
    setpremiumModalOpen(true);
  };

  const openFreeLimitUpsell = () => {
    setpremiumModalType("WARNING");
    setpremiumModalTitle(t("business.messages.freeLimitBlockTitle"));
    setpremiumModalDescription(t("business.messages.freeLimitBlockDesc"));
    setpremiumModalOpen(true);
  };

  // Build the edit-mode update input from current form state. Shared by Save
  // and by the save-then-init flow so the seeded chart matches what the user
  // sees (the backend reads structure/type/category off the Business record).
  const buildEditInput = () => {
    const input = {
      id,
      name,
      email,
      type: type?.value,
      structure: entityType?.value || null,
      category: category?.value || null,
      phone: cleanedPhone,
      taxCategory: taxCategory?.value || null,
      address,
      taxNumber: npwp,
      yearRegistered: yearRegistered?.value || null,
      fiscalYearStartMonth: fiscalYearStartMonth?.value || null,
      alwaysSync,
      cashIncomeDebitAccountID: alwaysSync ? cashIncomeDebitAccount?.value : null,
      cashIncomeCreditAccountID: alwaysSync
        ? cashIncomeCreditAccount?.value
        : null,
      cashExpenseDebitAccountID: alwaysSync
        ? cashExpenseDebitAccount?.value
        : null,
      cashExpenseCreditAccountID: alwaysSync
        ? cashExpenseCreditAccount?.value
        : null,
      salesDiscountAccountID: salesDiscountAccount?.value || null,
      purchaseDiscountAccountID: purchaseDiscountAccount?.value || null,
    };
    if (!isPremium) {
      input.image = null;
    } else if (image) {
      input.image = image;
    } else if (image === null) {
      input.image = null;
    }
    return input;
  };

  const handleInitAccount = () => {
    // Save-then-init: persist the current form profile first, then seed the
    // chart, so an unsaved category/type change is reflected. The update-
    // success effect chains requestInitAccount when initAfterSave is set.
    if (!isEditing || !business?.id) {
      requestInitAccount(accountingDispatch, { businessID: business?.id });
      return;
    }
    if (!validate()) return;
    setInitAfterSave(true);
    requestUpdateBusiness(dispatch, {
      input: buildEditInput(),
      ...(mainBusinessActive && { businessID: id }),
    });
  };

  const clearError = (field) =>
    setErrors((prev) => ({ ...prev, [field]: undefined }));

  const handleFieldChange = (setter, field) => (eventOrValue) => {
    const value =
      eventOrValue?.target !== undefined
        ? eventOrValue.target.value
        : eventOrValue;
    setter(value);
    clearError(field);
  };

  const validate = () => {
    const newErrors = {};

    if (!name) newErrors.name = t("business.errors.nameRequired");
    if (!type?.value) newErrors.type = t("business.errors.typeRequired");
    if (phone && cleanedPhone?.length < 10)
      newErrors.phone = t("business.errors.invalidPhone");
    if (email && !emailRegex.test(email))
      newErrors.email = t("business.errors.invalidEmail");
    if (npwp && (npwp.length < 15 || npwp.length > 16))
      newErrors.npwp = t("business.errors.invalidNpwp");

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const onSave = async (event) => {
    event.preventDefault();
    if (createBlocked) {
      openFreeLimitUpsell();
      return;
    }
    if (!validate()) return;

    if (isEditing) {
      requestUpdateBusiness(dispatch, {
        input: buildEditInput(),
        ...(mainBusinessActive && { businessID: id }),
      });
    } else {
      const created = await requestCreateBusiness(dispatch, {
        input: {
          name,
          email,
          type: type?.value,
          structure: entityType?.value || null,
          category: category?.value || null,
          phone: cleanedPhone,
          taxCategory: taxCategory?.value || null,
          address,
          taxNumber: npwp,
          yearRegistered: yearRegistered?.value || null,
          fiscalYearStartMonth: fiscalYearStartMonth?.value || null,
          alwaysSync,
        },
      });
      if (created?.id) handleCreated(created);
    }
  };

  const resetForm = () => {
    setname("");
    settype(BUSINESS_TYPES_OPTIONS[0]);
    setentityType(null);
    setcategory(null);
    setphone("");
    setaddress("");
    settaxCategory(null);
    setnpwp("");
    setyearRegistered(null);
    setfiscalYearStartMonth(null);
    setemail("");
    setimage(null);
    setimageError(null);
    setalwaysSync(false);
    setmainBusinessActive(false);
    setcashIncomeDebitAccount(null);
    setcashIncomeCreditAccount(null);
    setcashExpenseDebitAccount(null);
    setcashExpenseCreditAccount(null);
    setsalesDiscountAccount(null);
    setpurchaseDiscountAccount(null);
    setErrors({});
    onClose();
  };

  const renderProfileLogo = () => {
    if (!imageUrl) return <NoLogo />;
    return (
      <div className="relative rounded-lg w-24 h-24 flex-shrink-0 overflow-hidden">
        <img
          src={imageUrl}
          alt={t("business.messages.profileAlt")}
          className="absolute object-contain w-full h-full z-10"
          loading="lazy"
        />
        <div
          className="absolute w-full h-full blur-[20px] brightness-90 scale-110"
          style={{
            backgroundImage: `url('${imageUrl}')`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        />
      </div>
    );
  };

  const EmptyAccount = () =>
    initAccountLoading ? (
      <div className="space-y-4">
        <InputSkeleton rows={4} />
      </div>
    ) : (
      <EmptyState
        className="max-w-full"
        titleKey="business.emptyAccount.title"
        descriptionKey="business.emptyAccount.description"
      >
        <div className="flex flex-col justify-between gap-4">
          <Button
            variant="secondary"
            color="blue"
            className="flex-1"
            onClick={handleInitAccount}
          >
            {t("business.actions.initAccount")}
          </Button>
          <Button
            variant="secondary"
            color="blue"
            className="flex-1"
            onClick={() => navigate(`/account/import?id=${business.id}`)}
          >
            {t("business.actions.importAccount")}
          </Button>
        </div>
      </EmptyState>
    );

  return (
    <>
      <SlideOver
        open={open}
        onClose={resetForm}
        title={
          isEditing ? t("business.title.edit") : t("business.title.create")
        }
        actionButton={
          <Link
            to={isEditing ? `/business/${business?.id}` : "/business/create"}
            className="rounded-md bg-white text-gray-400 hover:text-gray-500"
            target="_blank"
            rel="noopener noreferrer"
          >
            <ExternalLinkIcon className="h-6 w-6" />
          </Link>
        }
        footer={
          <div className="flex justify-end gap-3">
            <Button
              variant="secondary"
              type="button"
              onClick={resetForm}
              disabled={loading}
            >
              {t("business.actions.cancel")}
            </Button>
            <Button
              type="submit"
              form="business-form"
              loading={loading}
              disabled={loading || businessError || createBlocked}
              data-testid="create-business"
            >
              {isEditing
                ? t("business.actions.save")
                : t("business.actions.create")}
            </Button>
          </div>
        }
      >
        <form id="business-form" action="#" onSubmit={onSave} className="mb-6">
          <FormError error={error || submitError || businessError} />
          {createBlocked && (
            <div className="mt-4 rounded-md border border-amber-200 bg-amber-50 p-4">
              <h3 className="text-sm font-semibold text-amber-800">
                {t("business.messages.freeLimitBlockTitle")}
              </h3>
              <p className="mt-1 text-sm text-amber-700">
                {t("business.messages.freeLimitBlockDesc")}
              </p>
              <button
                type="button"
                onClick={() => navigate("/subscription")}
                className="mt-3 inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                {t("business.actions.subscribe")}
              </button>
            </div>
          )}
          <Fieldset
            disabled={loading}
            className="divide-y divide-gray-200 data-[disabled]:opacity-50"
          >
            {/* ── Section 1: Data Perusahaan ── */}
            <SectionCard title={t("business.sections.companyData")}>
              <Fieldset className="space-y-4">
                <Field>
                  <FormInput
                    required
                    label="business.fields.name"
                    id="name"
                    name="name"
                    data-testid="organization"
                    value={name}
                    onChange={handleFieldChange(setname, "name")}
                    placeholder={t("business.placeholders.name")}
                    error={errors.name}
                  />
                </Field>
                <Field>
                  <FormRadioGroup
                    required
                    label="business.fields.type"
                    name="type"
                    options={BUSINESS_TYPES_OPTIONS}
                    value={type}
                    onChange={handleFieldChange(settype, "type")}
                    error={errors.type}
                  />
                </Field>
                <Field>
                  <FormSelect
                    label="business.fields.entityType"
                    id="entityType"
                    name="entityType"
                    options={BUSINESS_ENTITY_TYPES}
                    placeholder={t("business.placeholders.entityType")}
                    value={entityType}
                    onChange={handleFieldChange(setentityType, "entityType")}
                    disabled={type?.value !== "BUSINESS"}
                    isClearable
                    searchable
                    hint={t("common.optional")}
                  />
                </Field>
                <Field>
                  <FormSelect
                    label={t("business.fields.category", {
                      defaultValue: "Kategori Usaha",
                    })}
                    id="category"
                    name="category"
                    options={BUSINESS_CATEGORIES}
                    placeholder={t("business.placeholders.category", {
                      defaultValue: "Pilih Kategori Usaha",
                    })}
                    value={category}
                    onChange={handleFieldChange(setcategory, "category")}
                    isClearable
                    searchable
                    hint={t("common.optional")}
                  />
                </Field>
                <Field>
                  <FormSelect
                    label="business.fields.annualRevenue"
                    id="taxCategory"
                    name="taxCategory"
                    options={taxCategories}
                    value={taxCategory}
                    onChange={handleFieldChange(settaxCategory, "taxCategory")}
                    placeholder={t("business.placeholders.annualRevenue")}
                    isClearable
                    searchable
                    hint={t("common.optional")}
                  />
                </Field>
                <Field>
                  <FormInput
                    label="business.fields.npwp"
                    id="npwp"
                    name="npwp"
                    data-testid="npwp"
                    value={npwp || ""}
                    onChange={handleFieldChange(setnpwp, "npwp")}
                    placeholder={t("business.placeholders.npwp")}
                    error={errors.npwp}
                    numberFormat
                    allowLeadingZeros
                    isAllowed={({ value }) => value.length <= 16}
                    hint={t("common.optional")}
                  />
                </Field>
                <Field>
                  <FormSelect
                    label="business.fields.yearRegistered"
                    id="year"
                    name="year"
                    options={years}
                    placeholder={t("business.placeholders.yearRegistered")}
                    value={yearRegistered}
                    onChange={handleFieldChange(
                      setyearRegistered,
                      "yearRegistered",
                    )}
                    isClearable
                    searchable
                    hint={t("common.optional")}
                  />
                </Field>
                <Field>
                  <FormSelect
                    label={t("business.fields.fiscalYearStartMonth", {
                      defaultValue: "Awal Tahun Fiskal",
                    })}
                    id="fiscalYearStartMonth"
                    name="fiscalYearStartMonth"
                    options={fiscalMonthOptions}
                    placeholder={t("business.placeholders.fiscalYearStartMonth", {
                      defaultValue: "Januari",
                    })}
                    value={fiscalYearStartMonth}
                    onChange={handleFieldChange(
                      setfiscalYearStartMonth,
                      "fiscalYearStartMonth",
                    )}
                    isClearable
                    searchable
                    hint={t("common.optional")}
                  />
                </Field>
                <Field>
                  <FormInput
                    label="business.fields.email"
                    id="email"
                    name="email"
                    data-testid="email"
                    type="email"
                    value={email}
                    onChange={handleFieldChange(setemail, "email")}
                    placeholder={t("business.placeholders.email")}
                    hint={t("common.optional")}
                    error={errors.email}
                  />
                </Field>
                <Field>
                  <FormPhoneInput
                    label="business.fields.phone"
                    id="phone"
                    name="phone"
                    data-testid="phone"
                    value={phone}
                    onChange={handleFieldChange(setphone, "phone")}
                    placeholder={t("business.placeholders.phone")}
                    hint={t("common.optional")}
                    error={errors.phone}
                  />
                </Field>
                <Field>
                  <FormInput
                    label="business.fields.address"
                    id="address"
                    name="address"
                    data-testid="address"
                    value={address}
                    onChange={handleFieldChange(setaddress, "address")}
                    placeholder={t("business.placeholders.address")}
                    hint={t("common.optional")}
                  />
                </Field>
                {isEditing && (
                  <FormToggle
                    fieldLabel="business.fields.mainBusiness"
                    checked={mainBusinessActive}
                    onChange={setmainBusinessActive}
                    label="business.fields.mainBusinessLabel"
                  />
                )}
              </Fieldset>
            </SectionCard>

            {/* ── Section 2: Logo Perusahaan ── */}
            <SectionCard title={t("business.sections.companyLogo")}>
              <div className="flex gap-6 items-center">
                {renderProfileLogo()}
                <div>
                  <div className="flex items-center gap-3">
                    <div className="relative">
                      <div className="inline-flex items-center gap-2 bg-white py-2 px-3 border border-gray-300 rounded-md shadow-sm cursor-pointer hover:bg-gray-50">
                        <span className="text-sm font-medium text-gray-700">
                          {t("business.actions.changeLogo")}
                        </span>
                        {!isPremium && (
                          <span className="inline-flex items-center rounded px-2 py-0.5 text-xs font-bold bg-blue-600 text-white tracking-wide">
                            PREMIUM
                          </span>
                        )}
                      </div>
                      {isPremium ? (
                        <input
                          id="user-photo"
                          name="user-photo"
                          type="file"
                          accept="image/jpeg, image/png, image/gif"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onClick={(e) => {
                            e.target.value = "";
                          }}
                          onChange={handleProfileLogoChange}
                        />
                      ) : (
                        <button
                          type="button"
                          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                          onClick={handleNoPremium}
                        />
                      )}
                    </div>
                    {imageUrl && (
                      <button
                        onClick={() => setimage(null)}
                        type="button"
                        className="bg-transparent py-2 px-3 border border-transparent rounded-md text-sm font-medium text-gray-700 hover:text-gray-500 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 focus-visible:ring-offset-2"
                      >
                        {t("business.actions.removeLogo")}
                      </button>
                    )}
                  </div>
                  <p className="text-gray-500 text-xs mt-2">
                    {image?.name
                      ? image.name
                      : t("business.messages.logoFormat")}
                  </p>
                  {!isPremium && (
                    <p className="text-xs text-amber-600 mt-1">
                      {t("business.logo.freeUserWarning")}
                    </p>
                  )}
                </div>
              </div>
              {imageError && (
                <div className="mt-1 text-red-600 text-sm">{t(imageError)}</div>
              )}
            </SectionCard>

            {/* ── Section 3: Pengaturan Akun ── */}
            <SectionCard title={t("business.sections.accountSettings")}>
              <div className="space-y-4">
                <FormToggle
                  fieldLabel="business.fields.sync"
                  checked={alwaysSync}
                  onChange={setalwaysSync}
                  label="business.fields.syncLabel"
                />

                {/* Account mapping (edit only) — one section holding the
                    cash-book sub-group (sync only) and the discount sub-group
                    (always), sharing a single empty-state prompt. */}
                {isEditing &&
                  business?.id &&
                  (accountLoading || loading ? (
                    <InputSkeleton rows={alwaysSync ? 6 : 2} />
                  ) : accountError ? (
                    <FormError error={accountError} />
                  ) : accounts.length === 0 ? (
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                      <EmptyAccount />
                    </div>
                  ) : (
                    <div className="space-y-4 pt-4 border-t border-gray-100">
                      {alwaysSync && (
                        <div className="space-y-4">
                          <p className="text-sm font-medium text-gray-900">
                            {t("business.groups.cashBookMapping")}
                          </p>
                          <FormSelect
                            label="business.fields.debitIncome"
                            name="cashIncomeDebitAccount"
                            options={cashIncomeDebitOptions}
                            value={cashIncomeDebitAccount}
                            onChange={setcashIncomeDebitAccount}
                            placeholder="business.placeholders.debitAccount"
                            searchable
                          />
                          <FormSelect
                            label="business.fields.creditIncome"
                            name="cashIncomeCreditAccount"
                            options={cashIncomeCreditOptions}
                            value={cashIncomeCreditAccount}
                            onChange={setcashIncomeCreditAccount}
                            placeholder="business.placeholders.creditAccount"
                            searchable
                          />
                          <FormSelect
                            label="business.fields.debitExpense"
                            name="cashExpenseDebitAccount"
                            options={cashExpenseDebitOptions}
                            value={cashExpenseDebitAccount}
                            onChange={setcashExpenseDebitAccount}
                            placeholder="business.placeholders.debitAccount"
                            searchable
                          />
                          <FormSelect
                            label="business.fields.creditExpense"
                            name="cashExpenseCreditAccount"
                            options={cashExpenseCreditOptions}
                            value={cashExpenseCreditAccount}
                            onChange={setcashExpenseCreditAccount}
                            placeholder="business.placeholders.creditAccount"
                            searchable
                          />
                        </div>
                      )}
                      {/* Divider only when the cash-book sub-group is also shown. */}
                      <div
                        className={
                          alwaysSync
                            ? "space-y-4 pt-4 border-t border-gray-100"
                            : "space-y-4"
                        }
                      >
                        <div>
                          <p className="text-sm font-medium text-gray-900">
                            {t("business.groups.discountAccounts")}
                          </p>
                          <p className="text-xs text-gray-500 mt-0.5">
                            {t("business.groups.discountAccountsHelp")}
                          </p>
                        </div>
                        <FormSelect
                          label="business.fields.salesDiscountAccount"
                          name="salesDiscountAccount"
                          options={salesDiscountOptions}
                          value={salesDiscountAccount}
                          onChange={setsalesDiscountAccount}
                          placeholder="business.placeholders.discountAccount"
                          searchable
                        />
                        <FormSelect
                          label="business.fields.purchaseDiscountAccount"
                          name="purchaseDiscountAccount"
                          options={purchaseDiscountOptions}
                          value={purchaseDiscountAccount}
                          onChange={setpurchaseDiscountAccount}
                          placeholder="business.placeholders.discountAccount"
                          searchable
                        />
                      </div>
                    </div>
                  ))}
              </div>
            </SectionCard>
          </Fieldset>
        </form>
      </SlideOver>

      {/* navigate to premium subscription */}
      <ModalConfirm
        open={
          premiumModalOpen &&
          Boolean(premiumModalTitle) &&
          Boolean(premiumModalDescription)
        }
        setOpen={setpremiumModalOpen}
        title={premiumModalTitle}
        description={premiumModalDescription}
        type={premiumModalType}
        confirmTitle={t("business.actions.subscribe")}
        onConfirm={() => navigate("/subscription")}
      />
    </>
  );
};

export default BusinessFormModal;
